/* eslint-disable react/jsx-max-depth */
import React, { useCallback, useMemo } from 'react';
import {
  StyleSheet,
  Document,
  Page,
  View,
  Text,
  Image,
  Link,
} from '@react-pdf/renderer';
import Logo from './Logo';
// import { registerFonts } from '../../../utilities/pdf';
import { DataTypeIcon } from './DataTypeIcon';
import SaveBanner from './SaveBanner';
import { TickIcon } from './TickIcon';
import {
  DataType,
  getBaseDataType,
  isDerivedDataType,
} from '@larki/mp/lib/dataType';
import * as dateFns from 'date-fns';
import { groupBy, partitionBy } from '@larki/shared/object';
import { formatCurrencyForQuote, formatSqmArea } from '../../../text';

const COLOR = {
  DARKEST_GREY: '#2A2A2A',
  LIGHEST_GREY: '#F3F3F3',
  GREY1: '#4A4A4A',
  GREY2: '#696767',
  GREY4: '#DCDCDC',

  GREEN1: '#31A283',

  PINK: '#E55384',
  ORANGE: '#ec7100',

  TEAL: '#157daa',
  PURPLE: '#8e53ee',
  BLUE: '#3048c9',
};
const styles = StyleSheet.create({
  page: {
    padding: '32px 48px',
    fontFamily: 'Roboto',
    fontSize: '10px',
    lineHeight: '140%',
    color: COLOR.DARKEST_GREY,
  },
  pageCounter: {
    borderTop: `1px solid ${COLOR.GREY4}`,
    padding: '8px 4px',
    color: COLOR.GREY2,
    textAlign: 'right',
  },
});
const headerStyles = StyleSheet.create({
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'row',
    gap: '20px',
    alignItems: 'center',
  },
  headerRight: {},
  verticalDivider: {
    alignSelf: 'stretch',
    width: '1px',
    backgroundColor: COLOR.GREY1,
  },
  heading: {
    fontSize: '18px',
    lineHeight: '24px',
    fontFamily: 'Montserrat',
    fontWeight: 600,
    color: COLOR.GREY1,
  },
  contactInfo: {
    fontSize: '10px',
    lineHeight: '140%',
    fontFamily: 'Roboto',
    color: COLOR.DARKEST_GREY,
    textAlign: 'right',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  link: {
    color: COLOR.DARKEST_GREY,
    textDecoration: 'none',
  },
});
const metadataStyles = StyleSheet.create({
  metadata: { marginBottom: '16px' },
  heading: {
    fontWeight: 600,
    fontSize: '24px',
    fontFamily: 'Roboto',
    lineHeight: '32px',
    marginBottom: '16px',
    color: COLOR.GREEN1,
  },
  tableContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
    marginBottom: '10px',
  },
  table: {
    display: 'flex',
    flexDirection: 'column',
  },
  tableRow: {
    height: '14px',
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
    alignItems: 'center',
  },
  tableHeaderCell: {
    fontSize: '8px',
    color: COLOR.GREY2,
    fontWeight: 600,
    width: '70px',
    lineHeight: 1,
  },
  tableBodyCell: {
    fontSize: '10px',
    color: COLOR.DARKEST_GREY,
    lineHeight: 1,
  },
  link: {
    color: COLOR.DARKEST_GREY,
    textDecoration: 'none',
    fontWeight: 400,
  },
});
const quoteStyles = StyleSheet.create({
  quoteSection: {
    padding: '10px 0 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  quoteHeading: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '140%',
    color: COLOR.GREY1,
    padding: '4px 0',
  },
  summary: {
    borderTop: `1px solid ${COLOR.GREY2}`,
    fontWeight: 600,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
  },
});
const quoteItemBorderStyles = StyleSheet.create({
  exterior: {
    borderTop: `1px solid ${COLOR.PINK}`,
  },
  interior: {
    borderTop: `1px solid ${COLOR.ORANGE}`,
  },
  aerial: {
    borderTop: `1px solid ${COLOR.TEAL}`,
  },
  streetscape: {
    borderTop: `1px solid ${COLOR.BLUE}`,
  },
  drone: {
    borderTop: `1px solid ${COLOR.PURPLE}`,
  },
});
const quoteItemTextStyles = StyleSheet.create({
  exterior: {
    color: COLOR.PINK,
  },
  interior: {
    color: COLOR.ORANGE,
  },
  aerial: {
    color: COLOR.TEAL,
  },
  streetscape: {
    color: COLOR.BLUE,
  },
  drone: {
    color: COLOR.PURPLE,
  },
});
const quoteItemStyles = StyleSheet.create({
  root: {
    padding: '8px 0 0',
    gap: '8px',
    display: 'flex',
    flexDirection: 'column-reverse',
  },
  iconContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
  },
  scopeArea: {
    fontSize: '8px',
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
  },
  textContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    gap: '4px',
  },
  heading: {
    fontWeight: 600,
  },
  description: {
    paddingLeft: '20px',
    // flexWrap: 'wrap',
    // flexGrow: 1,
    // flexBasis: 0,
    // display: 'flex',
    // flexDirection: 'row',
    // flex: 1,
  },
  priceContent: {
    fontWeight: 600,
    // flexBasis: 'auto',
  },
});
const quoteSummaryStyles = StyleSheet.create({
  table: {
    fontWeight: 700,
    display: 'flex',
    flexDirection: 'row',
  },
  tableColumn: {
    // flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    flex: 1,
  },
  tableCell: {
    paddingVertical: '4px',
  },
  textLarge: {
    color: COLOR.GREEN1,
    fontSize: '16px',
    fontWeight: 700,
  },
});
const adStyles = StyleSheet.create({
  root: {
    borderRadius: '10px',
    backgroundColor: COLOR.LIGHEST_GREY,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  header: {
    fontSize: '14px',
    color: COLOR.PINK,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    color: COLOR.GREY1,
  },
  list: {
    marginVertical: '5px',
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '5px',
  },
  listItem: {
    display: 'flex',
    flexDirection: 'row',
    gap: '5px',
    alignItems: 'center',
  },
  footer: {
    textAlign: 'center',
  },
});

export type QuotePdfData = {
  type: 'estimate' | 'quote';
  metadata: {
    lastUpdateBy?: string | null;
    company?: string | null;
    email?: string | null;
    date?: Date | null;
    projectId?: number | null;
    jobId?: number | null;
  };
  projectName?: string | null;
  siteAddress: string;
  software?: string | null;
  comment?: string | null;
  products: {
    dataType: Exclude<DataType, 'unknown'>;
    deliveryMethod: 'automatic' | 'manual';
    scopeArea: number;
    price: number;
  }[];
  siteDeploymentFee?: number | null;
  totalNoTax: number;
  tax: number;
  total: number;
};

const TEXT = {
  TYPE_TO: {
    HEADING: {
      estimate: 'Estimate',
      quote: 'Quote',
    },
    DESCRIPTION: {
      estimate:
        'Please review this approximate price estimate below. Next, LARKI will do a formal quote. Should you have any queries, feel free to contact LARKI (details above).',
      quote: `Please review this Quote. Should you have an queries, feel free to contact LARKI (details above).
In accepting this Quote, you accept LARKI's Customer Terms & Conditions (updated from time to time a LARKI.au).
Please confirm your Survey Request, by emailing this Project ID and Order ID to solution@larki.au that you “ACCEPT”.
Payment terms are $1500+GST Site Deployment fee upfront in order to engage us, with the remainder paid prior to delivery of files.

We are here to help you make land surveys, site measure-ups and existing condition information exceptionally detailed 3D. This can help you save project time, save project money in the long run and make the most out of your site.

Looking forward to your return email go-head, so we can get started for you.`,
    },
    TOTAL: {
      estimate: 'Estimate Price:',
      quote: 'Quoted Price:',
    },
  },
  DELIVERY_METHOD_TO: {
    HEADING: {
      automatic: '3D Data in the Database',
      manual: '3D Scan Capture Services',
    },
  },
  DATA_TYPE_TO: {
    HEADING: {
      aerial: 'Aerial 3D Point Cloud',
      streetscape: 'Streetscape 3D Point Cloud',
      drone: 'Drone 3D Point Cloud',
      exterior: 'Exterior Tripod 3D Point Cloud',
      interior: 'Interior Tripod 3D Point Cloud',
      bim_exterior: 'Exterior BIM',
      bim_interior: 'Interior BIM',
      twod_plan_exterior: 'Exterior 2D Feature Plan',
      twod_plan_interior: 'Interior 2D Feature Plan',
    } satisfies Record<Exclude<DataType, 'unknown'>, string>,
    DESCRIPTION: {
      aerial:
        '3D features seen from ~3000m above, output from a 3D laser scanner mounted on a plane flying over.',
      streetscape:
        'Medium resolution, colourised 3D point cloud data, captured from a 3D laser scanner on a car (LiDAR).',
      drone:
        '3D features seen from ~40m above, output from a 3D laser scanner mounted on a drone flying over.',
      exterior:
        'High resolution (<20mm point spacing), colourised, laser scan data. For BIM enabled architects, this 3D point cloud can be used instead of a site measure-up, so you can design with precision to maximise site yield.',
      interior:
        'High resolution (<20mm point spacing), colourised, laser scan data. For BIM enabled architects, this 3D point cloud can be used instead of a site measure-up, so you can design with precision to maximise site yield.',
      bim_exterior:
        'An extraction of the main features from the 3D Point Cloud, this file is a basic best-fit model of the visible interior floors only, optimised for Building Information Model (BIM) software. The accuracy is limited to obtainable surface measurements only.',
      bim_interior:
        'An extraction of the main features from the 3D Point Cloud, this file is a basic best-fit model of the visible interior floors only, optimised for Building Information Model (BIM) software. The accuracy is limited to obtainable surface measurements only.',
      twod_plan_exterior:
        'An extraction of the main features from the 3D Point Cloud, this file is a basic best-fit model of the visible interior floors only, optimised for Building Information Model (BIM) software. The accuracy is limited to obtainable surface measurements only.',
      twod_plan_interior:
        'An extraction of the main features from the 3D Point Cloud, this file is a basic best-fit model of the visible interior floors only, optimised for Building Information Model (BIM) software. The accuracy is limited to obtainable surface measurements only.',
    } satisfies Record<Exclude<DataType, 'unknown'>, string>,
  },
  ADD_ONS: 'Add-ons',
};

export const TEST_DATA: QuotePdfData = {
  type: 'estimate',
  metadata: {
    lastUpdateBy: 'Georgie Archer',
    company: 'AA Architects',
    email: 'georgie@aaarchitects.com.au',
    date: new Date('2024-06-09T12:41:00'),
    projectId: 1234,
    jobId: 1234,
  },
  siteAddress: '188 Johnston Street, Collingwood, Victoria, 3066',
  products: [
    {
      deliveryMethod: 'automatic',
      dataType: 'aerial',
      price: 200,
      scopeArea: 15000,
    },
    {
      deliveryMethod: 'automatic',
      dataType: 'streetscape',
      price: 200,
      scopeArea: 15000,
    },
    {
      deliveryMethod: 'automatic',
      dataType: 'drone',
      price: 200,
      scopeArea: 15000,
    },
    {
      deliveryMethod: 'manual',
      dataType: 'exterior',
      price: 2000,
      scopeArea: 15000,
    },
    {
      deliveryMethod: 'manual',
      dataType: 'interior',
      price: 2000,
      scopeArea: 15000,
    },
    {
      deliveryMethod: 'manual',
      dataType: 'bim_exterior',
      price: 2000,
      scopeArea: 15000,
    },
    {
      deliveryMethod: 'manual',
      dataType: 'bim_interior',
      price: 2000,
      scopeArea: 15000,
    },
    {
      deliveryMethod: 'manual',
      dataType: 'twod_plan_exterior',
      price: 2000,
      scopeArea: 15000,
    },
    {
      deliveryMethod: 'manual',
      dataType: 'twod_plan_interior',
      price: 2000,
      scopeArea: 15000,
    },
  ],
  siteDeploymentFee: 1500,
  totalNoTax: 12100,
  tax: 1210,
  total: 13310,
};

const iconMap: Record<Exclude<DataType, 'unknown'>, () => React.ReactNode> = {
  exterior: () => <DataTypeIcon.Exterior width={16} height={16} />,
  interior: () => <DataTypeIcon.Interior width={16} height={16} />,
  aerial: () => <DataTypeIcon.Aerial width={16} height={16} />,
  streetscape: () => <DataTypeIcon.Streetscape width={16} height={16} />,
  drone: () => <DataTypeIcon.Drone width={16} height={16} />,
  bim_exterior: () => <DataTypeIcon.BimExterior width={16} height={16} />,
  bim_interior: () => <DataTypeIcon.BimInterior width={16} height={16} />,
  twod_plan_exterior: () => (
    <DataTypeIcon.TwoDPlanExterior width={16} height={16} />
  ),
  twod_plan_interior: () => (
    <DataTypeIcon.TwoDPlanInterior width={12} height={12} />
  ),
};

function MetadataRow<T extends React.ReactNode>({ label, value }: { label: string, value?: T | null }) {
  if (!value) return null;
  return (
    <View style={metadataStyles.tableRow}>
      <Text style={metadataStyles.tableHeaderCell}>
        {label}
      </Text>
      <Text style={metadataStyles.tableBodyCell}>
        {value}
      </Text>
    </View>
  )
}

export type QuotePdfProps = { data: QuotePdfData };
export const PdfGenerator = ({ data }: QuotePdfProps) => {
  const { totalNoTax, tax, total, quoteSections, timeAndDate } = useMemo(() => {
    const [addOnProducts, standardProducts] = partitionBy(data.products, (p) =>
      isDerivedDataType(p.dataType)
    );
    const quoteSections: {
      title: string;
      products: QuotePdfData['products'];
      siteDeploymentFee?: number | null;
    }[] = [];
    if (standardProducts.length) {
      quoteSections.push(
        ...[...groupBy(standardProducts, 'deliveryMethod')].map(
          ([deliveryMethod, products]) => {
            const siteDeploymentFee =
              deliveryMethod === 'manual' ? data.siteDeploymentFee : null;
            return {
              title: TEXT.DELIVERY_METHOD_TO.HEADING[deliveryMethod],
              products,
              siteDeploymentFee,
            };
          }
        )
      );
    }
    if (addOnProducts.length) {
      quoteSections.push({
        title: TEXT.ADD_ONS,
        products: addOnProducts,
      });
    }
    return {
      totalNoTax: formatCurrencyForQuote(data.totalNoTax, 'aud'),
      tax: formatCurrencyForQuote(data.tax, 'aud'),
      total: formatCurrencyForQuote(data.total, 'aud'),
      quoteSections,
      timeAndDate: data.metadata.date ? dateFns.format(data.metadata.date, 'HH:mmaaa, dd MMM yyyy') : null, // format like 12:41pm, 09 Jun 2024
    };
  }, [data]);

  const pageNumbers = useCallback(
    ({
      pageNumber,
      totalPages,
    }: {
      pageNumber: number;
      totalPages: number;
    }) => {
      console.log(pageNumber, totalPages);
      return `${pageNumber} / ${totalPages}`;
    },
    []
  );

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <View style={headerStyles.header}>
          <View style={headerStyles.headerLeft}>
            <Logo width={130} />
            <View style={headerStyles.verticalDivider} />
            <Text style={headerStyles.heading}>3D Surveys</Text>
          </View>
          <View style={headerStyles.headerRight}>
            <View style={headerStyles.contactInfo}>
              <Text>LARKI Australia Pty Ltd</Text>
              <Text>ABN: 84 646 447 896</Text>
              <Text>P: 1800 3D SCAN</Text>
              <Text>E: solutions@larki.au</Text>
              <Link href='https://www.larki.com.au' style={headerStyles.link}>
                www.larki.au
              </Link>
            </View>
          </View>
        </View>
        <View style={metadataStyles.metadata}>
          <Text style={metadataStyles.heading}>{TEXT.TYPE_TO.HEADING[data.type]}</Text>
          <View style={metadataStyles.tableContainer}>
            <View style={{ ...metadataStyles.table, flexGrow: 1 }}>
              <MetadataRow label="Last Update By:" value={data.metadata.lastUpdateBy} />
              <MetadataRow label="Company:" value={data.metadata.company} />
              <MetadataRow label="Email:" value={data.metadata.email} />
            </View>
            <View style={metadataStyles.table}>
              <MetadataRow label="Time & Date:" value={timeAndDate} />
              <MetadataRow label="Project ID:" value={data.metadata.projectId} />
              <MetadataRow label="Order ID:" value={data.metadata.jobId} />
            </View>
          </View>
          <View style={metadataStyles.table}>
            <MetadataRow label="Project:" value={data.projectName} />
            <View style={metadataStyles.tableRow}>
              <Text style={metadataStyles.tableHeaderCell}>Site Address:</Text>
              <Text
                style={{ ...metadataStyles.tableBodyCell, fontWeight: 700 }}
              >
                <Link
                  href={`https://www.google.com/maps/place/${data.siteAddress.replace(/\s+/g, '+')}`}
                  style={metadataStyles.link}
                >
                  {data.siteAddress}
                </Link>
              </Text>
            </View>
            <MetadataRow label="Software:" value={data.software} />
            <MetadataRow label="Comment:" value={data.comment} />
          </View>
        </View>
        <Text>{TEXT.TYPE_TO.DESCRIPTION[data.type]}</Text>
        {quoteSections.map(({ title, products, siteDeploymentFee }) => {
          if (products.length === 0) return null;
          return (
            <View style={quoteStyles.quoteSection} key={title}>
              <Text style={quoteStyles.quoteHeading}>{title}</Text>
              {products.map((product, i) => {
                const Icon = iconMap[product.dataType];
                const baseDataType = isDerivedDataType(product.dataType)
                  ? getBaseDataType(product.dataType)
                  : product.dataType;
                const scopeArea = formatSqmArea(product.scopeArea);
                const price = formatCurrencyForQuote(product.price, 'aud');
                return (
                  <View
                    style={{
                      ...quoteItemStyles.root,
                      ...quoteItemBorderStyles[baseDataType],
                    }}
                    wrap={false}
                    key={i}
                  >
                    <Text style={quoteItemStyles.scopeArea}>
                      Scope Area: {scopeArea}
                    </Text>
                    <View style={quoteItemStyles.content}>
                      <View style={quoteItemStyles.textContent}>
                        <View style={quoteItemStyles.header}>
                          <View style={quoteItemStyles.iconContainer}>
                            <Icon />
                          </View>
                          <Text
                            style={{
                              ...quoteItemStyles.heading,
                              ...quoteItemTextStyles[baseDataType],
                            }}
                          >
                            {TEXT.DATA_TYPE_TO.HEADING[product.dataType]}
                          </Text>
                        </View>
                        <Text style={quoteItemStyles.description}>
                          {TEXT.DATA_TYPE_TO.DESCRIPTION[product.dataType]}
                        </Text>
                      </View>
                      <View style={quoteItemStyles.priceContent}>
                        <Text>{price}</Text>
                      </View>
                    </View>
                  </View>
                );
              })}
              {/* <View style={{ ...quoteItemStyles.root, ...quoteItemStyles.interiorBorder }} wrap={false}>
                <Text style={quoteItemStyles.scopeArea}>
                  Scope Area: 15,000 m²
                </Text>
                <View style={quoteItemStyles.content}>
                  <View style={quoteItemStyles.textContent}>
                    <View style={quoteItemStyles.header}>
                      <View style={quoteItemStyles.iconContainer}>
                        <DataTypeIcon.Interior width={16} height={16} />
                      </View>
                      <Text style={{ ...quoteItemStyles.heading, ...quoteItemStyles.interiorText }}>
                        Interior Tripod 3D Point Cloud
                      </Text>
                    </View>
                    <Text style={quoteItemStyles.description}>
                      {`High resolution (<20mm point spacing), colourised, laser scan data. For BIM enabled architects, this 3D point cloud can be used instead of a site measure-up, so you can design with precision to maximise site yield.`}
                    </Text>
                  </View>
                  <View style={quoteItemStyles.priceContent}>
                    <Text>A$ 2,000</Text>
                  </View>
                </View>
              </View> */}
              {siteDeploymentFee ? (
                <View style={quoteStyles.summary}>
                  <Text>Site Deployment Fee</Text>
                  <Text>A$ 1,500</Text>
                </View>
              ) : null}
            </View>
          );
        })}

        {/* <View style={quoteStyles.quoteSection}>
          <Text style={quoteStyles.quoteHeading}>
            Add-ons
          </Text>
          <View style={{ ...quoteItemStyles.root, ...quoteItemStyles.exteriorBorder }} wrap={false}>
            <Text style={quoteItemStyles.scopeArea}>
              Scope Area: 15,000 m²
            </Text>
            <View style={quoteItemStyles.content}>
              <View style={quoteItemStyles.textContent}>
                <View style={quoteItemStyles.header}>
                  <View style={quoteItemStyles.iconContainer}>
                    <DataTypeIcon.BimExterior width={16} height={16} />
                  </View>
                  <Text style={{ ...quoteItemStyles.heading, ...quoteItemStyles.exteriorText }}>
                    Exterior BIM (3D Building Information Model)
                  </Text>
                </View>
                <Text style={quoteItemStyles.description}>

                </Text>
              </View>
              <View style={quoteItemStyles.priceContent}>
                <Text>A$ 2,000</Text>
              </View>
            </View>
          </View>
          <View style={{ ...quoteItemStyles.root, ...quoteItemStyles.interiorBorder }} wrap={false}>
            <Text style={quoteItemStyles.scopeArea}>
              Scope Area: 15,000 m²
            </Text>
            <View style={quoteItemStyles.content}>
              <View style={quoteItemStyles.textContent}>
                <View style={quoteItemStyles.header}>
                  <View style={quoteItemStyles.iconContainer}>
                    <DataTypeIcon.TwoDPlanInterior width={12} height={12} />
                  </View>
                  <Text style={{ ...quoteItemStyles.heading, ...quoteItemStyles.interiorText }}>
                    2D Feature Plan - Interior
                  </Text>
                </View>
                <Text style={quoteItemStyles.description}>

                </Text>
              </View>
              <View style={quoteItemStyles.priceContent}>
                <Text>A$ 2,000</Text>
              </View>
            </View>
          </View>*/}

        <View style={{ paddingTop: '24px', marginBottom: 'auto' }}>
          <View
            style={{
              borderTop: `1px solid ${COLOR.GREY2}`,
              fontWeight: 600,
              paddingVertical: '4px',
            }}
            wrap={false}
          >
            <View style={quoteSummaryStyles.table}>
              <View style={quoteSummaryStyles.tableColumn}>
                <Text style={quoteSummaryStyles.tableCell}>Subtotal:</Text>
                <Text style={quoteSummaryStyles.tableCell}>GST:</Text>
                <Text
                  style={{
                    ...quoteSummaryStyles.tableCell,
                    ...quoteSummaryStyles.textLarge,
                  }}
                >
                  {TEXT.TYPE_TO.TOTAL[data.type]}
                </Text>
              </View>
              <View style={quoteSummaryStyles.tableColumn}>
                <Text style={quoteSummaryStyles.tableCell}>{totalNoTax}</Text>
                <Text style={quoteSummaryStyles.tableCell}>{tax}</Text>
                <Text
                  style={{
                    ...quoteSummaryStyles.tableCell,
                    ...quoteSummaryStyles.textLarge,
                  }}
                >
                  {total}
                </Text>
              </View>
            </View>
          </View>
        </View>

        <View style={adStyles.root} wrap={false}>
          <View style={adStyles.header}>
            <Text>
              Add <Text style={{ fontWeight: 600 }}>Essentials Membership</Text>{' '}
              to save on this project
            </Text>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '6px',
                alignItems: 'center',
              }}
            >
              <SaveBanner />
              <Text style={{ fontWeight: 700 }}>A$ 1,500</Text>
            </View>
          </View>
          <View style={adStyles.content}>
            <Image src='/public/img/viewer-in-laptop.png' style={{ flex: 1 }} />
            <View style={{ flex: 1 }}>
              <Text style={{ fontWeight: 600 }}>And more!</Text>
              <Text>Get 3D, measure & collaborate online:</Text>
              <View style={adStyles.list}>
                <View style={adStyles.listItem}>
                  <TickIcon width={12} height={12} />
                  <Text>Save 30% on 3D Data purchases online</Text>
                </View>
                <View style={adStyles.listItem}>
                  <TickIcon width={12} height={12} />
                  <Text>Save 10% on 3D Scan & BIM services</Text>
                </View>
                <View style={adStyles.listItem}>
                  <TickIcon width={12} height={12} />
                  <Text>High-res 2D Map aerial imagery</Text>
                </View>
                <View style={adStyles.listItem}>
                  <TickIcon width={12} height={12} />
                  <Text>3D Viewer measurements & more!</Text>
                </View>
              </View>
              <Text>For $90+GST monthly, or $900+GST yearly.</Text>
            </View>
          </View>
          <Text style={adStyles.footer}>
            Contact us to add the Membership - email{' '}
            <Text style={{ fontWeight: 600 }}>solutions@larki.au</Text> or call{' '}
            <Text style={{ fontWeight: 600 }}>1800 33 7226</Text>
          </Text>
        </View>
        {/* <View render={({pageNumber}) => (
        	<Text>
            	{pageNumber == 1 ? 'FIRST PAGE' : 'Strictly confidential'}
          	</Text>  
        )} fixed style={styles.pageCounter}/> */}
      </Page>
    </Document>
  );
};

const PdfDownloadLink = () => {
  return null;
};
